import { ApiError, isNetworkError } from '@liniya/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { queryKeys } from '../query-keys';
import { preference } from '../platform';

/** Reuse the same device across sign-ins so the device list stays meaningful. */
const DEVICE_STORAGE_KEY = 'liniya.device-id';

function rememberedDeviceId(): string | undefined {
  return preference().get(DEVICE_STORAGE_KEY) ?? undefined;
}

// Without it the account simply gains a new device entry, which is the correct
// fallback rather than a failure.
function rememberDeviceId(deviceId: string): void {
  preference().set(DEVICE_STORAGE_KEY, deviceId);
}

/**
 * Turn an error into something worth showing a person.
 *
 * Credential failures deliberately stay vague — the server does not reveal
 * whether an address exists, and neither should the interface.
 */
export function describeAuthError(error: unknown): string {
  if (isNetworkError(error)) return 'Нет связи с сервером. Проверьте подключение.';
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'invalid_credentials':
        return 'Неверный email или пароль.';
      case 'rate_limited':
        return 'Слишком много попыток. Попробуйте позже.';
      case 'account_exists':
        return 'Аккаунт с таким email уже существует.';
      case 'device_limit_reached':
        return 'Достигнут лимит устройств. Отзовите одно из них в настройках.';
      case 'temporarily_unavailable':
        return 'Сервис временно недоступен. Попробуйте через минуту.';
      case 'invalid_account_data':
      case 'invalid_request': {
        const fields = Object.values(error.fieldErrors()).flat();
        return fields[0] ?? 'Проверьте введённые данные.';
      }
      default:
        return error.message || 'Не удалось выполнить вход.';
    }
  }
  return 'Не удалось выполнить вход.';
}

export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const deviceId = rememberedDeviceId();
      return await api.auth.signIn({
        email: credentials.email,
        password: credentials.password,
        token_delivery: 'cookie',
        device: {
          platform: 'web',
          name: 'Веб-браузер',
          ...(deviceId ? { id: deviceId } : {}),
        },
      });
    },
    onSuccess: async (pair) => {
      rememberDeviceId(pair.device.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useRegister() {
  const signIn = useSignIn();

  return useMutation({
    mutationFn: async (input: { email: string; password: string; confirmation: string }) => {
      await api.auth.register({
        email: input.email,
        password: input.password,
        password_confirmation: input.confirmation,
      });
      // Registering already proves the credentials, so the new account is
      // taken straight into a session rather than back to a sign-in form.
      return await signIn.mutateAsync({ email: input.email, password: input.password });
    },
  });
}
